import {isFeatureEnabled} from 'feature-flags';
import {Nullish} from 'utility-types';

import {isNotNil} from 'ramda';

import {useGetParticipationQuery} from '@dms/api/core';
import {EntityResourceIds} from '@dms/api/shared';
import {SaleVehicleResponseBody} from '@dms/api/vehicle';
import {featureFlags} from '@dms/feature-flags';
import {EntityHeaderControl, usePermissions, useVehicleWarehouse} from '@dms/shared';

import {buildArray} from 'shared/web';

interface UseGetVehicleControlsProps {
  isSaleVehicle: boolean;
  isServiceVehicle: boolean;
  vehicleId: string;
  saleVehicle: SaleVehicleResponseBody | Nullish;
  branchId: string | Nullish;
}

export function useGetVehicleControls(props: UseGetVehicleControlsProps) {
  const {isVehicleWarehouseEnabled} = useVehicleWarehouse({
    vehicleId: props.vehicleId,
  });

  const {data: vehicleParticipation} = useGetParticipationQuery({
    recordId: props.vehicleId,
    resourceId: EntityResourceIds.vehicle,
  });

  const [hasVehicleReadWarehouseInfoPermission, canReadAdvertisingSettings, canReadServiceCase] =
    usePermissions({
      permissionKeys: [
        'vehicleReadWarehouseInformation',
        'readAdvertisingSettings',
        'serviceCaseRead',
      ],
      scopes: {
        readAdvertisingSettings: {
          participation: vehicleParticipation,
          branchId: props.branchId,
        },
      },
    });

  if (isFeatureEnabled(featureFlags.VEHICLE_HEADER_V3)) {
    return buildArray<EntityHeaderControl>(['PARKING', 'KEY', 'ASSIGNEE', 'VH_CUSTOM_ID'])
      .when(props.isServiceVehicle, 'SERVICE_BRANCH')
      .when(props.isSaleVehicle, 'VEHICLE_LOCATION')
      .when(
        isNotNil(props.saleVehicle) &&
          isVehicleWarehouseEnabled &&
          hasVehicleReadWarehouseInfoPermission,
        'VEHICLE_WAREHOUSE'
      )
      .when(
        isNotNil(props.saleVehicle) &&
          canReadAdvertisingSettings &&
          isFeatureEnabled(featureFlags.SALES_SALE_PROGRAMS),
        'SALE_PRESET'
      );
  }

  if (!props.isSaleVehicle && canReadServiceCase) {
    return buildArray<EntityHeaderControl>([
      'PARKING',
      'SERVICE_BRANCH',
      'ASSIGNEE',
      'VH_CUSTOM_ID',
    ]);
  }

  return buildArray<EntityHeaderControl>([
    'ASSIGNEE',
    isVehicleWarehouseEnabled ? 'VEHICLE_LOCATION' : 'LOCATION',
  ])
    .when(
      isNotNil(props.saleVehicle) &&
        canReadAdvertisingSettings &&
        isFeatureEnabled(featureFlags.SALES_SALE_PROGRAMS),
      'SALE_PRESET'
    )
    .when(isNotNil(props.saleVehicle), 'PARKING')
    .when(isNotNil(props.saleVehicle), 'KEY')
    .when(
      isVehicleWarehouseEnabled &&
        isNotNil(props.saleVehicle) &&
        hasVehicleReadWarehouseInfoPermission,
      'VEHICLE_WAREHOUSE'
    )
    .add('VH_CUSTOM_ID');
}
